import React, { useState, useRef, useEffect } from "react";

const ProportionalSlider = ({ segments, onSlideEnd }) => {
    const [proportions, setProportions] = useState([]);
    const containerRef = useRef(null);
    const [dragIndex, setDragIndex] = useState(null);
    const [startX, setStartX] = useState(0);
    const [startProportions, setStartProportions] = useState(null);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        // if (!initialized) {
            const total = segments.reduce((sum, seg) => sum + seg.percentage, 0);
            const normalized = segments.map((seg) => ({
                ...seg,
                percentage: (seg.percentage / total) * 100,
            }));

            setProportions(normalized);
            setInitialized(true); // Ensure this runs only once unless segments change externally
        // }
    }, [segments, initialized]);

    const adjustProportions = (updatedProportions) => {
        // Redistribute the remaining percentages to other segments
        const total = updatedProportions.reduce((sum, prop) => sum + prop.percentage, 0);
        const diff = 100 - total;

        if (Math.abs(diff) > 0.01) {
            for (let i = 0; i < updatedProportions.length; i++) {
                if (i !== dragIndex && updatedProportions[i].percentage > 0) {
                    updatedProportions[i].percentage += diff;
                    break;
                }
            }
        }

        return updatedProportions.map((prop) => ({
            ...prop,
            percentage: Math.max(0, Math.min(100, prop.percentage)),
        }));
    };

    const handleDragStart = (index, e) => {
        e.preventDefault();
        setDragIndex(index);
        setStartX(e.clientX);
        setStartProportions([...proportions]);
    };

    const handleDrag = (e) => {
        if (dragIndex === null || !startProportions) return;

        const containerWidth = containerRef.current.offsetWidth;
        const movementPercent = ((e.clientX - startX) / containerWidth) * 100;

        const newProportions = startProportions.map((prop, i) => {
            if (i === dragIndex) {
                return {
                    ...prop,
                    percentage: Math.max(0, Math.min(100, prop.percentage + movementPercent)),
                };
            }
            if (i === dragIndex + 1) {
                return {
                    ...prop,
                    percentage: Math.max(0, Math.min(100, prop.percentage - movementPercent)),
                };
            }
            return prop;
        });

        setProportions(adjustProportions(newProportions));
    };

    const handleDragEnd = () => {
        setProportions((prevProportions) => {
            const roundedProportions = adjustProportions(prevProportions).map((prop) => ({
                ...prop,
                percentage: prop.percentage,
            }));
            if (onSlideEnd) {
                onSlideEnd(roundedProportions);
            }
            return roundedProportions;
        });
        setDragIndex(null);
        setStartProportions(null);
    };


    useEffect(() => {
        if (dragIndex !== null) {
            window.addEventListener("mousemove", handleDrag);
            window.addEventListener("mouseup", handleDragEnd);
            return () => {
                window.removeEventListener("mousemove", handleDrag);
                window.removeEventListener("mouseup", handleDragEnd);
            };
        }
        // eslint-disable-next-line
    }, [dragIndex, startX, startProportions]);

    return (
        <div className="mb-3">
            <div
                ref={containerRef}
                className="position-relative"
                style={{
                    height: "48px",
                    borderRadius: "8px",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
                    backgroundColor: "#e9ecef",
                }}
            >
                {proportions.map((segment, index) => {
                    const leftPosition = proportions
                        .slice(0, index)
                        .reduce((sum, seg) => sum + seg.percentage, 0);

                    const isFirst = index === 0;
                    const isLast = index === proportions.length - 1;

                    return (
                        <div
                            key={segment.name}
                            className="position-absolute h-100 d-flex align-items-center justify-content-center"
                            style={{
                                left: `${leftPosition}%`,
                                width: `${segment.percentage}%`,
                                backgroundColor: segment.color,
                                borderRadius: `${isFirst ? "8px" : "0"} ${isLast ? "8px" : "0"} ${isLast ? "8px" : "0"} ${isFirst ? "8px" : "0"}`,
                            }}
                        >
                            <span className="text-white small">
                                {`${segment.name} (${segment.percentage.toFixed(0)}%)`}
                            </span>
                            {index < proportions.length - 1 && (
                                <div
                                    className="position-absolute h-100 bg-white"
                                    style={{
                                        right: "-8px",
                                        width: "16px",
                                        cursor: "ew-resize",
                                        boxShadow: "0 0 4px rgba(0,0,0,0.2)",
                                        zIndex: 10,
                                    }}
                                    onMouseDown={(e) => handleDragStart(index, e)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ProportionalSlider;